import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonRow,
  IonText,
  IonTextarea,
  IonTitle,
  IonToolbar,
  IonInput,
} from "@ionic/react";
import React, { useContext, useRef, useState } from "react";
import { TranslationsContext } from "../util/Translations";
import "./WOApprovalModal.css";
import {
  PurchaseOrderApprovalLine,
  WorkOrderActivity,
  WorkOrderMetadata,
} from "../models/workorders/WorkOrder";
import {
  searchOutline,
  searchSharp,
  timerOutline,
} from "ionicons/icons";
import POApprovalLogActivity from "./POApprovalLogActivity";
import {
  ProcessPurchaseOrderData,
  processPurchaseOrders,
} from "../api/MobileApproval";
import { ApiResponse } from "../models/api/ApiResponse";
import { getValueSearchDetails } from "../api/QueryDesigner";
import FilterModal from "./FilterModal";
import { findEmployee } from "../api/Employees";
import ISSPopover from "./ISSPopover";

interface POModalProps {
  isOpen: boolean;
  cancel: () => void;
  approve: (status: string) => void;
  review: (status: string) => void;
  reject: (status: string) => void;
  cardHeader: string;
  metadata: WorkOrderMetadata;
  poLines: PurchaseOrderApprovalLine[];
  poActivity: WorkOrderActivity[];
  nextReviewer: string;
  setNextReviewer: (nextReviewer: string) => void;
  setShowLoading: (showLoading: boolean) => void;
  backdropDismiss: false;
}

const POApprovalModal: React.FC<POModalProps> = (props) => {
  const {
    isOpen,
    cancel,
    approve,
    review,
    reject,
    cardHeader,
    poLines,
    poActivity,
    nextReviewer,
    setNextReviewer,
    setShowLoading,
    backdropDismiss,
  } = props;
  const { translations } = useContext(TranslationsContext);
  const [comments, setComments] = useState<string>("");
  const [showLogActivity, setShowLogActivity] = useState(false);
  const approveAction = "approve";
  const reviewAction = "review";
  const rejectAction = "reject";
  // Next reviewer is chosen via employee type-ahead: unlike WO/Req there is no
  // PurchaseOrderReviewer code list, so we search em_employee directly and seed
  // the field with the user's DefaultPOReviewer.
  const [filterModalColumnName, setFilterModalColumnName] = useState("");
  const [filterModalModuleName, setFilterModalModuleName] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const popover = useRef<HTMLIonPopoverElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");

  const onApprove = async () => {
    processApproval(approveAction);
  };

  const onReview = async () => {
    processApproval(reviewAction);
  };

  const onReject = async () => {
    processApproval(rejectAction);
  };

  const processApproval = async (action: string) => {
    setShowLoading(true);
    const data: ProcessPurchaseOrderData = {
      Action: action,
      Comments: comments,
      NextReviewer: nextReviewer,
      PurchaseOrder: cardHeader,
    };
    processPurchaseOrders([data]).then((response) => {
      handleStatusMessage(
        action,
        response.data?.PoInfoResult[0]?.MessageList[0]?.Text
      );
      setShowLoading(false);
    });
  };

  const handleStatusMessage = async (action: string, status: string) => {
    if (action === approveAction) {
      return approve(status);
    }
    return action === reviewAction ? review(status) : reject(status);
  };

  const resetSearchState = () => {
    setFilterModalColumnName("");
    setFilterModalModuleName("");
  };

  const onFilterModalConfirm = (
    selectedItem: string | number | null | undefined
  ) => {
    setParameter(filterModalColumnName, selectedItem);
    setIsFilterModalOpen(false);
    resetSearchState();
  };

  const setParameter = (
    paramKey: string,
    paramValue: string | number | null | undefined
  ) => {
    if (paramKey === "employee_id") {
      setNextReviewer(paramValue as string);
    }
  };

  const handleEmployeeSearch = (e: CustomEvent) => {
    let newVal = (e.target as HTMLInputElement)?.value;
    setNextReviewer(newVal);
    if (newVal.length > 2) {
      findEmployee(newVal).then((response) => {
        if (response.status === 200) {
          if (Object.keys(response.data).length !== 0) {
            popover.current!.event = e;
            setPopoverOpen(true);
          } else if (popoverOpen) {
            setPopoverOpen(false);
          }
          let empSearchArray = [];
          const employees = Object.entries(response.data).map(([id, name]) => ({
            EmployeeId: id,
            EmployeeName: name,
          }));
          for (let search of employees) {
            empSearchArray.push(search.EmployeeId);
          }
          setSearchResults(empSearchArray);
        }
      });
    } else {
      if (popoverOpen) {
        setPopoverOpen(false);
      }
      if (searchResults.length > 0) {
        setSearchResults([]);
      }
    }
  };

  return (
    <IonModal isOpen={isOpen} backdropDismiss={backdropDismiss}>
      <FilterModal
        isOpen={isFilterModalOpen}
        moduleName={filterModalModuleName}
        cancel={() => setIsFilterModalOpen(false)}
        confirm={onFilterModalConfirm}
      />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={cancel}>
              {translations["lbl_btn_cancel"] || "Cancel"}
            </IonButton>
          </IonButtons>
          <IonTitle>
            {translations["lbl_mob_po_approval"] || "Purchase Order Approval"}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonGrid>
          <IonRow>
            <IonCol>
              <IonText>
                <h3 className="ion-no-margin">&nbsp;{cardHeader}</h3>
              </IonText>
            </IonCol>
          </IonRow>
        </IonGrid>
        {poLines.map((line, index) => {
          return (
            <IonCard key={index}>
              <IonCardHeader>
                <IonCardTitle class="card-title">
                  {translations["lbl_popurchaseorderline_line"] || "Line"}
                  &nbsp;{line.LineNum}
                </IonCardTitle>
                <IonCardSubtitle>{line.ItemDesc}</IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                <IonRow>
                  <IonCol>
                    {translations["lbl_popurchaseorderline_item"] || "Item"}
                  </IonCol>
                  <IonCol>{line.Item}</IonCol>
                </IonRow>
                <IonRow>
                  <IonCol>
                    {translations["lbl_popurchaseorderline_quantity_ordered"] ||
                      "Quantity"}
                  </IonCol>
                  <IonCol>{line.Qty}</IonCol>
                </IonRow>
                <IonRow>
                  <IonCol>
                    {translations["lbl_popurchaseorderline_unit_cost"] ||
                      "Unit Cost"}
                  </IonCol>
                  <IonCol>{line.UnitCost}</IonCol>
                </IonRow>
                <IonRow>
                  <IonCol>
                    {translations["lbl_popurchaseorderline_extended_cost"] ||
                      "Extended Cost"}
                  </IonCol>
                  <IonCol>{line.ExtCost}</IonCol>
                </IonRow>
              </IonCardContent>
            </IonCard>
          );
        })}
        <IonGrid class="comment-title">
          <IonRow>
            <IonCol>
              <IonText>
                <h3 className="ion-no-margin">
                  {translations["lbl_incyclecountbatch_comment"] || "Comment"}
                </h3>
              </IonText>
            </IonCol>
            <IonCol>
              <IonButton
                class="log"
                fill="clear"
                size="small"
                disabled={poActivity.length === 0}
                onClick={() => {
                  setShowLogActivity(true);
                }}
              >
                <IonIcon slot="start" icon={timerOutline} size="small" />
                {translations["lbl_log_activity"] || "Log Activity"}
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>
        <IonCard>
          <IonTextarea
            class="approval-textarea"
            placeholder={translations["lbl_enter comments"] || "Enter Comments"}
            autoGrow={true}
            onIonChange={(ev) => {
              setComments(String(ev.detail.value));
            }}
          ></IonTextarea>
        </IonCard>
        <IonItem>
          <IonLabel>
            {translations["lbl_popurchaseorder_nextreviewer"] ||
              "Next Reviewer"}
          </IonLabel>
          <IonInput
            class="ion-text-end"
            value={nextReviewer}
            onIonInput={(ev) => {
              onFilterModalConfirm(ev.target.value as string);
              handleEmployeeSearch(ev);
            }}
          ></IonInput>
          <IonIcon
            slot="end"
            ios={searchOutline}
            md={searchSharp}
            onClick={async () => {
              resetSearchState();
              setFilterModalColumnName("employee_id");
              const response: ApiResponse = await getValueSearchDetails({
                tableName: "em_employee",
                columnName: "employee_id",
              });
              if (response.status === 200) {
                setFilterModalModuleName(response.data.QueryWizardModuleName);
                setIsFilterModalOpen(true);
              }
            }}
          ></IonIcon>
        </IonItem>
        <IonButton expand="block" onClick={onApprove}>
          {translations["lbl_approve"] || "Approve"}
        </IonButton>
        <IonButton fill="outline" expand="block" onClick={onReview}>
          {translations["lbl_review"] || "Review"}
        </IonButton>
        <IonButton
          fill="outline"
          color="danger"
          expand="block"
          onClick={onReject}
        >
          {translations["lbl_reject"] || "Reject"}
        </IonButton>
        <POApprovalLogActivity
          isOpen={showLogActivity}
          dismiss={() => setShowLogActivity(false)}
          setShowLoading={setShowLoading}
          poActivity={poActivity}
        />
      </IonContent>
      <ISSPopover
        popover={popover}
        popoverOpen={popoverOpen}
        setPopoverOpen={setPopoverOpen}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        setElementValue={setNextReviewer}
      />
    </IonModal>
  );
};

export default POApprovalModal;
